import { createContext, useContext, ReactNode } from 'react';
import { useHomeAreas, Area } from '../hooks/useHomeAreas';

// Define the shape of the context
interface HomeAreasContextProps {
  areas: Area[];
  isLoading: boolean;
  error: string | null;
  fetchAreas: () => Promise<void>;
  updateAreaDetails: (
    areaName: string, 
    updates: { area_pro?: string; area_con?: string; area_overall?: string }
  ) => Promise<{ success: boolean; error?: string }>;
  setError: (error: string | null) => void;
  homeId?: string;
  lastUpdated: number;
}

// Create the context
export const HomeAreasContext = createContext<HomeAreasContextProps | undefined>(undefined);

interface HomeAreasProviderProps {
  children: ReactNode;
  homeId: string;
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

export function HomeAreasProvider({ 
  children, 
  homeId,
  onSuccess,
  onError
}: HomeAreasProviderProps) {
  // Use the hook with the homeId override
  const areasData = useHomeAreas({
    homeIdOverride: homeId,
    onSuccess,
    onError,
    showNotifications: false // Default to not showing notifications for background refreshes
  });
  
  return (
    <HomeAreasContext.Provider value={{ 
      ...areasData,
      homeId 
    }}>
      {children}
    </HomeAreasContext.Provider>
  );
}

// Custom hook to use the context
export function useHomeAreasContext() {
  const context = useContext(HomeAreasContext);
  if (context === undefined) {
    throw new Error('useHomeAreasContext must be used within a HomeAreasProvider');
  }
  return context;
}